import React, { useState, useEffect } from "react";

/**
 * Lightbox component for viewing photos/videos fullscreen with navigation
 *
 * @param {Array} files - Array of file objects with { url, name, type }
 * @param {number} currentIndex - Current file index being viewed
 * @param {function} onClose - Callback when lightbox is closed
 * @param {function} onNavigate - Callback when navigating to different index
 * @param {function} getImageUrl - Optional function to transform image URLs (e.g., Cloudinary)
 */
export default function Lightbox({ files, currentIndex, onClose, onNavigate, getImageUrl }) {
  const file = files[currentIndex];
  const isVideo = file?.type !== "image";

  // Transform URL if function provided, otherwise use raw URL
  const imageUrl = getImageUrl ? getImageUrl(file?.url) : file?.url;

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && currentIndex > 0) onNavigate(currentIndex - 1);
      if (e.key === "ArrowRight" && currentIndex < files.length - 1) onNavigate(currentIndex + 1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, files.length, onClose, onNavigate]);

  // Handle touch swipe
  const [touchStart, setTouchStart] = useState(null);
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentIndex < files.length - 1) onNavigate(currentIndex + 1);
      if (diff < 0 && currentIndex > 0) onNavigate(currentIndex - 1);
    }
    setTouchStart(null);
  };

  // Download current file
  const handleDownload = async () => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || `download_${currentIndex + 1}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao baixar arquivo:", err);
      // Fallback: open in new tab
      window.open(file.url, "_blank");
    }
  };

  if (!file) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          background: "rgba(255,255,255,0.2)",
          border: "none",
          color: "white",
          fontSize: "28px",
          width: "50px",
          height: "50px",
          borderRadius: "50%",
          cursor: "pointer",
          zIndex: 10001,
        }}
        title="Fechar (ESC)"
      >
        ✕
      </button>

      {/* Download button */}
      <button
        onClick={(e) => { e.stopPropagation(); handleDownload(); }}
        style={{
          position: "absolute",
          top: "20px",
          right: "80px",
          background: "rgba(255,255,255,0.2)",
          border: "none",
          color: "white",
          fontSize: "20px",
          width: "50px",
          height: "50px",
          borderRadius: "50%",
          cursor: "pointer",
          zIndex: 10001,
        }}
        title="Download"
      >
        ⬇️
      </button>

      {/* Counter */}
      <div
        style={{
          position: "absolute",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          color: "white",
          fontSize: "16px",
          backgroundColor: "rgba(0,0,0,0.5)",
          padding: "8px 16px",
          borderRadius: "20px",
        }}
      >
        {currentIndex + 1} / {files.length}
      </div>

      {/* Previous button */}
      {currentIndex > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex - 1); }}
          style={{
            position: "absolute",
            left: "20px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "white",
            fontSize: "32px",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            cursor: "pointer",
            zIndex: 10001,
          }}
          title="Anterior (←)"
        >
          ‹
        </button>
      )}

      {/* Next button */}
      {currentIndex < files.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(currentIndex + 1); }}
          style={{
            position: "absolute",
            right: "20px",
            top: "50%",
            transform: "translateY(-50%)",
            background: "rgba(255,255,255,0.2)",
            border: "none",
            color: "white",
            fontSize: "32px",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            cursor: "pointer",
            zIndex: 10001,
          }}
          title="Próxima (→)"
        >
          ›
        </button>
      )}

      {/* Content */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "90vw",
          maxHeight: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isVideo ? (
          <video
            src={file.url}
            controls
            autoPlay
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        ) : (
          <img
            src={imageUrl}
            alt={file.name}
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
            }}
          />
        )}
      </div>

      {/* File name */}
      <div
        style={{
          position: "absolute",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          color: "white",
          fontSize: "14px",
          backgroundColor: "rgba(0,0,0,0.5)",
          padding: "8px 16px",
          borderRadius: "20px",
          maxWidth: "80%",
          textAlign: "center",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {file.name}
      </div>
    </div>
  );
}
